/**
 * Copilot slug → indexed-entity-type resolver.
 *
 * The knowledge-service entity index (`/knowledge/entities/{search,similar}`)
 * only knows a fixed set of REAL entity types (the ones the backfill embeds):
 *
 *   risk · policy · audit · obligation · control · vendor · assessment ·
 *   incident · objective   (+ governance_instance / case for future indexing)
 *
 * The generic CrudPage used to pass `entity_type=<first-path-segment>`
 * (e.g. `governance-pro`, `risk-pro`) to `/knowledge/entities/similar`, which
 * is NOT an indexed type → 404. This maps a route/module to a REAL indexed
 * entity type so "Similaires" + "Suggestions IA" hit the index correctly, and
 * returns `null` when there is no sensible mapping so the caller can hide the
 * affordance (degrade gracefully) instead of firing a 404.
 */

/** The canonical set of entity types the knowledge index actually embeds. */
export const INDEXED_ENTITY_TYPES = [
  'risk',
  'policy',
  'audit',
  'obligation',
  'control',
  'vendor',
  'assessment',
  'incident',
  'objective',
  // Reserved for upcoming index coverage — safe to request (empty, not 404-noise).
  'governance_instance',
  'case',
] as const

export type IndexedEntityType = (typeof INDEXED_ENTITY_TYPES)[number]

/**
 * Exact full-path matches (`seg0[/seg1[/seg2]]`, lower-cased, no leading slash).
 * These take precedence over the per-segment heuristic and disambiguate the
 * shared `register` slug (risk vs compliance vs governance).
 */
const FULL_PATH_MAP: Record<string, IndexedEntityType> = {
  // Risk
  'risk-pro/register': 'risk',
  'risk-pro/risks': 'risk',
  'risk-pro/incidents': 'incident',
  'risk-pro/controls': 'control',
  'governance-pro/risk-register': 'risk',
  // Compliance
  'compliance-pro/register': 'obligation',
  'compliance-pro/obligations': 'obligation',
  'compliance-pro/controls': 'control',
  // Governance
  'governance-pro/policies': 'policy',
  'governance-pro/controls-audit': 'control',
  'policy-mgmt/policies': 'policy',
  // Audit
  'audit-pro/missions': 'audit',
  // Vendor
  'vendor-pro/catalogue': 'vendor',
  // Security
  'security-pro/incidents': 'incident',
  'security-pro/controls': 'control',
  // Strategy
  'strategy-pro/objectives': 'objective',
  // Single-segment modules
  'governance-instances': 'governance_instance',
  'case-mgmt': 'case',
}

/**
 * Per-segment heuristic — matched against the LAST meaningful path segment,
 * EXACTLY (never substring) so tool routes like `data-product-catalog` or
 * `supply-chain-cyber-risk` do NOT accidentally resolve to `vendor` / `risk`.
 */
const SEGMENT_MAP: Record<string, IndexedEntityType> = {
  risks: 'risk',
  policies: 'policy',
  obligations: 'obligation',
  controls: 'control',
  missions: 'audit',
  audits: 'audit',
  catalogue: 'vendor',
  vendors: 'vendor',
  incidents: 'incident',
  objectives: 'objective',
  assessments: 'assessment',
}

/**
 * Default "link target" for the SuggestLinks affordance: given a source entity
 * type, the most useful type to propose linking to (risk→control, etc.).
 */
const DEFAULT_LINK_TARGET: Record<string, IndexedEntityType> = {
  risk: 'control',
  control: 'risk',
  policy: 'control',
  obligation: 'control',
  audit: 'control',
  vendor: 'risk',
  incident: 'risk',
  assessment: 'risk',
  objective: 'risk',
  governance_instance: 'policy',
  case: 'obligation',
}

/**
 * Resolve a REAL indexed entity type for a route path, or `null` when there is
 * no sensible mapping (caller should then hide the copilot affordance).
 */
export function resolveEntityType(pathname: string): IndexedEntityType | null {
  const segs = (pathname || '')
    .split('/')
    .filter(Boolean)
    .map((s) => s.toLowerCase())
  if (segs.length === 0) return null

  const p3 = segs.slice(0, 3).join('/')
  const p2 = segs.slice(0, 2).join('/')
  const p1 = segs[0]
  if (FULL_PATH_MAP[p3]) return FULL_PATH_MAP[p3]
  if (FULL_PATH_MAP[p2]) return FULL_PATH_MAP[p2]
  if (FULL_PATH_MAP[p1]) return FULL_PATH_MAP[p1]

  const last = segs[segs.length - 1]
  if (SEGMENT_MAP[last]) return SEGMENT_MAP[last]

  return null
}

/** Best default target type to suggest links toward for a given source type. */
export function defaultLinkTarget(entityType: string | null | undefined): IndexedEntityType {
  if (entityType && DEFAULT_LINK_TARGET[entityType]) return DEFAULT_LINK_TARGET[entityType]
  return 'control'
}

/** Human-friendly FR label for an entity type (for badges / selectors). */
export function entityTypeLabel(entityType: string): string {
  const labels: Record<string, string> = {
    risk: 'Risque',
    control: 'Contrôle',
    policy: 'Politique',
    obligation: 'Obligation',
    audit: 'Audit',
    vendor: 'Fournisseur',
    assessment: 'Évaluation',
    incident: 'Incident',
    objective: 'Objectif',
    governance_instance: 'Instance de gouvernance',
    case: 'Dossier',
    control_test: 'Test de contrôle',
    evidence: 'Preuve',
  }
  return labels[entityType] ?? entityType
}
